// ============================================================================
// LIT-CASE — Respondents section (ADR-0001 §3.1.3)
// ============================================================================
// Respondents in the litigation. Prefilled (copied) from the associated
// accounts' borrowers / guarantors, then independently editable — respondents
// may differ from the notice recipients. Card list with inline expand-to-edit,
// Prefill-from-accounts, and Add / Remove.
//
// Backend: caseOppositePartyService. Prefill source: litigationCaseService.getParties.

import React, { useCallback, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useCaseOppositeParties } from 'hooks/api/useCaseOppositeParties';
import { useCaseAdvocates } from 'hooks/api/useCaseAdvocates';
import { caseOppositePartyService } from 'api/services/caseOppositePartyService';
import { caseAdvocateService } from 'api/services/caseAdvocateService';
import { litigationCaseService } from 'api/services/litigationCaseService';
import type { CaseOppositeParty, OppositePartyRole } from 'api/types/caseOppositeParty.types';
import type { CaseAdvocate } from 'api/types/caseAdvocate.types';
import type { LitigationCase } from 'api/types/litigationCase.types';

interface Props {
  kase: LitigationCase;
  onMutated: () => void;
}

const ROLE_OPTS: { value: OppositePartyRole; label: string }[] = [
  { value: 'BORROWER', label: 'Borrower' },
  { value: 'CO_BORROWER', label: 'Co-Borrower' },
  { value: 'GUARANTOR', label: 'Guarantor' },
  { value: 'THIRD_PARTY', label: 'Third Party' },
];
const ROLE_CHIP: Record<string, { bg: string; fg: string }> = {
  BORROWER: { bg: '#dbeafe', fg: '#1e40af' },
  CO_BORROWER: { bg: '#ccfbf1', fg: '#115e59' },
  GUARANTOR: { bg: '#fef3c7', fg: '#92400e' },
  THIRD_PARTY: { bg: 'var(--bg-secondary)', fg: 'var(--text-secondary)' },
};
const roleLabel = (r: string) => ROLE_OPTS.find((o) => o.value === r)?.label ?? r;

// Map a case party (PRIMARY/CO_BORROWER/GUARANTOR) to a respondent role.
const partyRoleToRespondent = (r: string): OppositePartyRole =>
  r === 'GUARANTOR' ? 'GUARANTOR' : r === 'CO_BORROWER' ? 'CO_BORROWER' : 'BORROWER';

type Draft = {
  partyName: string; partyRole: OppositePartyRole;
  addressLine1: string; addressLine2: string; city: string; state: string; pincode: string;
  phone: string; email: string; relationshipToPrimary: string;
  // Respondent counsel (side=OPPOSITE advocate linked to this respondent).
  counselId: string; counselName: string; counselFirm: string; counselPhone: string;
};
const EMPTY: Draft = {
  partyName: '', partyRole: 'BORROWER', addressLine1: '', addressLine2: '',
  city: '', state: '', pincode: '', phone: '', email: '', relationshipToPrimary: '',
  counselId: '', counselName: '', counselFirm: '', counselPhone: '',
};

const CaseRespondentsSection: React.FC<Props> = ({ kase, onMutated }) => {
  const { data: respondents, loading, error, refetch } = useCaseOppositeParties(kase.id);
  const { data: advocates, refetch: refetchAdv } = useCaseAdvocates(kase.id);

  const counselFor = useCallback(
    (respondentId: string): CaseAdvocate | undefined =>
      advocates.find((a) => a.side === 'OPPOSITE' && a.oppositePartyId === respondentId),
    [advocates],
  );

  const [openId, setOpenId] = useState<string | null>(null); // existing row being edited
  const [isNew, setIsNew] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<CaseOppositeParty | null>(null);

  const toDraft = useCallback((r: CaseOppositeParty): Draft => {
    const c = counselFor(r.id);
    return {
      partyName: r.partyName, partyRole: r.partyRole,
      addressLine1: r.addressLine1 ?? '', addressLine2: r.addressLine2 ?? '',
      city: r.city ?? '', state: r.state ?? '', pincode: r.pincode ?? '',
      phone: r.phone ?? '', email: r.email ?? '', relationshipToPrimary: r.relationshipToPrimary ?? '',
      counselId: c?.id ?? '', counselName: c?.advocateName ?? '',
      counselFirm: c?.firmName ?? '', counselPhone: c?.phone ?? '',
    };
  }, [counselFor]);

  const closeEditor = useCallback(() => { setOpenId(null); setIsNew(false); setDraft(EMPTY); }, []);

  const openAdd = useCallback(() => { setDraft(EMPTY); setIsNew(true); setOpenId('__new__'); }, []);
  const openEdit = useCallback((r: CaseOppositeParty) => {
    if (openId === r.id) { closeEditor(); return; }
    setDraft(toDraft(r)); setIsNew(false); setOpenId(r.id);
  }, [openId, closeEditor, toDraft]);

  const save = useCallback(async () => {
    if (!draft.partyName.trim()) { notificationService.toast.warning('Respondent name is required.'); return; }
    const counselFilled = !!(draft.counselName.trim() || draft.counselFirm.trim() || draft.counselPhone.trim());
    if (counselFilled && !draft.counselName.trim()) {
      notificationService.toast.warning('Counsel name is required when adding respondent counsel.'); return;
    }
    setBusy(true);
    try {
      const payload = {
        partyName: draft.partyName.trim(), partyRole: draft.partyRole,
        addressLine1: draft.addressLine1 || undefined, addressLine2: draft.addressLine2 || undefined,
        city: draft.city || undefined, state: draft.state || undefined, pincode: draft.pincode || undefined,
        phone: draft.phone || undefined, email: draft.email || undefined,
        relationshipToPrimary: draft.relationshipToPrimary || undefined,
      };
      let respondentId: string | null = openId;
      if (isNew) {
        const created = await caseOppositePartyService.create({ caseId: kase.id, displayOrder: 0, ...payload });
        respondentId = created.data?.id ?? null;
      } else if (openId) {
        const existing = respondents.find((r) => r.id === openId);
        await caseOppositePartyService.update(openId, { ...payload, displayOrder: existing?.displayOrder ?? 100, rowVersion: existing?.rowVersion ?? 1 });
      }

      // Respondent counsel (side=OPPOSITE advocate linked to this respondent).
      if (respondentId && counselFilled) {
        const counselPayload = {
          side: 'OPPOSITE' as const, oppositePartyId: respondentId,
          advocateName: draft.counselName.trim(),
          firmName: draft.counselFirm || undefined, phone: draft.counselPhone || undefined,
        };
        if (draft.counselId) {
          const existingC = advocates.find((a) => a.id === draft.counselId);
          await caseAdvocateService.update(draft.counselId, {
            ...counselPayload, displayOrder: existingC?.displayOrder ?? 100, rowVersion: existingC?.rowVersion ?? 1,
          } as any);
        } else {
          await caseAdvocateService.create({ caseId: kase.id, displayOrder: 0, ...counselPayload } as any);
        }
      }

      notificationService.toast.success('Respondent saved.');
      closeEditor(); await refetch(); await refetchAdv(); onMutated();
    } catch (e: any) {
      notificationService.toast.error(e?.response?.data?.message ?? 'Failed to save respondent.', 6000);
    } finally { setBusy(false); }
  }, [draft, isNew, openId, respondents, advocates, kase.id, closeEditor, refetch, refetchAdv, onMutated]);

  const remove = useCallback(async () => {
    if (!removeTarget) return;
    setBusy(true);
    try {
      await caseOppositePartyService.delete(removeTarget.id);
      notificationService.toast.success('Respondent removed.');
      setRemoveTarget(null); await refetch(); onMutated();
    } catch (e: any) {
      notificationService.toast.error(e?.response?.data?.message ?? 'Failed to remove.', 6000);
    } finally { setBusy(false); }
  }, [removeTarget, refetch, onMutated]);

  const prefill = useCallback(async () => {
    setIsSeeding(true);
    try {
      const parties = await litigationCaseService.getParties(kase.id);
      const existingNames = new Set(respondents.map((r) => r.partyName.trim().toLowerCase()));
      const fresh = parties.filter((p) => p.customerName && !existingNames.has(p.customerName.trim().toLowerCase()));
      if (fresh.length === 0) {
        notificationService.toast.info('No new borrowers / guarantors to add.');
        return;
      }
      for (const p of fresh) {
        await caseOppositePartyService.create({
          caseId: kase.id, displayOrder: 0,
          partyName: p.customerName,
          partyRole: partyRoleToRespondent(p.partyRole),
          addressLine1: p.addressLine1 ?? undefined, addressLine2: p.addressLine2 ?? undefined,
          city: p.city ?? undefined, state: p.stateName ?? undefined, pincode: p.pincode ?? undefined,
          phone: p.mobilePrimary ?? undefined, email: p.email ?? undefined,
        });
      }
      notificationService.toast.success(`Added ${fresh.length} respondent${fresh.length === 1 ? '' : 's'} from the accounts.`);
      await refetch(); onMutated();
    } catch (e: any) {
      notificationService.toast.error(e?.response?.data?.message ?? 'Prefill failed.', 6000);
    } finally { setIsSeeding(false); }
  }, [kase.id, respondents, refetch, onMutated]);

  if (loading && respondents.length === 0) {
    return <div style={{ padding: '1.5rem', color: 'var(--text-secondary)' }}>Loading respondents…</div>;
  }
  if (error) {
    return <div style={{ padding: '1.5rem', color: '#dc2626' }}>Failed to load: {error.message}</div>;
  }

  return (
    <div style={{ padding: '1rem 1.25rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '0.95rem' }}>Respondents ({respondents.length})</h3>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
            Prefill from the associated accounts, then edit as required — respondents may differ from the notice recipients.
          </span>
        </div>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          <button type="button" className="btn btn-secondary" style={smallBtn} disabled={isSeeding} onClick={() => void prefill()}>
            <IconSystem.refresh size={12} style={{ marginRight: '0.3rem' }} />{isSeeding ? 'Loading…' : 'Prefill from accounts'}
          </button>
          <button type="button" className="btn btn-secondary" style={smallBtn} onClick={openAdd}>
            <IconSystem.add size={12} style={{ marginRight: '0.3rem' }} />Add Respondent
          </button>
        </div>
      </div>

      {/* New respondent editor */}
      {isNew && openId === '__new__' && (
        <div style={{ ...cardStyle, ...cardOpen, marginBottom: '0.5rem' }}>
          <div style={{ padding: '0.6rem 0.75rem' }}>
            <div style={subLabel}>New Respondent</div>
            <RespondentForm draft={draft} setDraft={setDraft} />
            <div style={actions}>
              <button type="button" className="btn btn-secondary" style={smallBtn} onClick={closeEditor}>Discard</button>
              <button type="button" className="btn btn-primary" style={smallBtn} disabled={busy} onClick={() => void save()}>Save</button>
            </div>
          </div>
        </div>
      )}

      {respondents.length === 0 && !isNew ? (
        <div style={emptyStyle}>No respondents yet. Use “Prefill from accounts” to populate.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {respondents.map((r) => {
            const chip = ROLE_CHIP[r.partyRole] ?? ROLE_CHIP.THIRD_PARTY;
            const addr = [r.addressLine1, r.city, r.state, r.pincode].filter(Boolean).join(', ');
            const isOpen = openId === r.id;
            const counsel = counselFor(r.id);
            return (
              <div key={r.id} style={{ ...cardStyle, ...(isOpen ? cardOpen : null) }}>
                <div style={rowHeader}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', minWidth: 0, flex: 1 }}>
                    <span style={{ ...roleChip, background: chip.bg, color: chip.fg }}>{roleLabel(r.partyRole)}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={nameStyle}>{r.partyName}</div>
                      {addr && <div style={addrStyle}>{addr}</div>}
                      {counsel?.advocateName && (
                        <div style={{ ...addrStyle, color: '#2563eb' }}>Counsel: {counsel.advocateName}</div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                    <button type="button" style={iconBtn} title="Edit respondent" onClick={() => openEdit(r)}>
                      <IconSystem.edit size={13} />
                    </button>
                    <button type="button" style={{ ...iconBtn, color: '#dc2626' }} title="Remove respondent" onClick={() => setRemoveTarget(r)}>
                      <IconSystem.trash size={13} />
                    </button>
                  </div>
                </div>
                {isOpen && (
                  <div style={{ padding: '0.2rem 0.75rem 0.7rem', borderTop: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
                    <RespondentForm draft={draft} setDraft={setDraft} />
                    <div style={actions}>
                      <button type="button" className="btn btn-secondary" style={smallBtn} onClick={closeEditor}>Cancel</button>
                      <button type="button" className="btn btn-primary" style={smallBtn} disabled={busy} onClick={() => void save()}>Save</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!removeTarget}
        title="Remove Respondent"
        message={`Remove ${removeTarget?.partyName ?? 'this respondent'} from the litigation?`}
        confirmLabel="Remove" cancelLabel="Cancel" variant="danger"
        loading={busy} loadingMessage="Removing…"
        onConfirm={() => void remove()} onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
};

const RespondentForm: React.FC<{ draft: Draft; setDraft: React.Dispatch<React.SetStateAction<Draft>> }> = ({ draft, setDraft }) => {
  const set = (k: keyof Draft, v: string) => setDraft((d) => ({ ...d, [k]: v }));
  const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.55rem 0.75rem' };
  return (
    <>
      <div style={grid}>
        <F label="Name *"><input className="form-input" value={draft.partyName} onChange={(e) => set('partyName', e.target.value)} /></F>
        <F label="Role"><SyutSelect options={ROLE_OPTS} value={draft.partyRole} onChange={(v) => set('partyRole', (v as string) || 'BORROWER')} isClearable={false} size="sm" /></F>
        <F label="Relationship"><input className="form-input" value={draft.relationshipToPrimary} onChange={(e) => set('relationshipToPrimary', e.target.value)} /></F>
        <F label="Address Line 1" full><input className="form-input" value={draft.addressLine1} onChange={(e) => set('addressLine1', e.target.value)} /></F>
        <F label="Address Line 2" full><input className="form-input" value={draft.addressLine2} onChange={(e) => set('addressLine2', e.target.value)} /></F>
        <F label="City"><input className="form-input" value={draft.city} onChange={(e) => set('city', e.target.value)} /></F>
        <F label="State"><input className="form-input" value={draft.state} onChange={(e) => set('state', e.target.value)} /></F>
        <F label="Pincode"><input className="form-input" value={draft.pincode} onChange={(e) => set('pincode', e.target.value)} /></F>
        <F label="Phone"><input className="form-input" value={draft.phone} onChange={(e) => set('phone', e.target.value)} /></F>
        <F label="Email"><input className="form-input" value={draft.email} onChange={(e) => set('email', e.target.value)} /></F>
      </div>
      <div style={subLabel}>Respondent Counsel <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></div>
      <div style={grid}>
        <F label="Counsel Name"><input className="form-input" value={draft.counselName} onChange={(e) => set('counselName', e.target.value)} /></F>
        <F label="Firm"><input className="form-input" value={draft.counselFirm} onChange={(e) => set('counselFirm', e.target.value)} /></F>
        <F label="Phone"><input className="form-input" value={draft.counselPhone} onChange={(e) => set('counselPhone', e.target.value)} /></F>
      </div>
    </>
  );
};

const F: React.FC<{ label: string; full?: boolean; children: React.ReactNode }> = ({ label, full, children }) => (
  <div className="form-group" style={{ margin: 0, gridColumn: full ? '1 / -1' : undefined }}>
    <label className="form-label" style={{ fontSize: '0.74rem', marginBottom: '0.2rem' }}>{label}</label>
    {children}
  </div>
);

// ---- styles ----
const smallBtn: React.CSSProperties = { padding: '0.25rem 0.65rem', fontSize: '0.78rem' };
const cardStyle: React.CSSProperties = { border: '1px solid var(--border-color)', borderRadius: 8, background: 'var(--bg-primary)', overflow: 'hidden' };
const cardOpen: React.CSSProperties = { borderColor: '#2563eb', boxShadow: '0 0 0 1px #2563eb22' };
const rowHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.6rem', padding: '0.55rem 0.7rem' };
const roleChip: React.CSSProperties = { fontSize: '0.7rem', fontWeight: 700, padding: '0.1rem 0.5rem', borderRadius: 999, whiteSpace: 'nowrap', flexShrink: 0 };
const nameStyle: React.CSSProperties = { fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const addrStyle: React.CSSProperties = { fontSize: '0.72rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '44ch' };
const iconBtn: React.CSSProperties = { border: 'none', background: 'transparent', cursor: 'pointer', padding: '0.25rem', color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center' };
const subLabel: React.CSSProperties = { fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', margin: '0.2rem 0 0.5rem' };
const actions: React.CSSProperties = { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.7rem' };
const emptyStyle: React.CSSProperties = { padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)', border: '1px dashed var(--border-color)', borderRadius: 6, fontSize: '0.85rem' };

export default CaseRespondentsSection;
